import { z } from "zod";
import { Rng } from "../rng.js";
import type { GameModule, GeneratedVariant, Hint, SettingsField, ValidationResult } from "../types.js";

// Починка роботом: игрок набирает программу из шагов и команды «Ремонт»,
// робот исполняет её по секторам диска. Наезд на разрушенный сектор — провал.

export const COMMANDS = ["up", "down", "left", "right", "fix"] as const;
export type Command = (typeof COMMANDS)[number];

export const MOVES: Record<Exclude<Command, "fix">, [number, number]> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

export type SectorType = "ok" | "bad" | "weak" | "fixed";

const pointSchema = z.object({ x: z.number().int().min(0), y: z.number().int().min(0) });

export const robotRepairSettingsSchema = z.object({
  cols: z.number().int().min(3).max(12).default(7),
  rows: z.number().int().min(3).max(12).default(7),
  start: pointSchema.default({ x: 0, y: 0 }),
  stackCapacity: z.number().int().min(4).max(64).default(30),
  badMin: z.number().int().min(0).default(8),
  badMax: z.number().int().min(0).default(11),
  weakMin: z.number().int().min(1).default(3),
  weakMax: z.number().int().min(1).default(5),
  solutionMin: z.number().int().min(1).default(10),
  solutionMax: z.number().int().min(1).default(24),
  stepMs: z.number().int().min(80).max(1200).default(420),
  edgeBehavior: z.enum(["bump", "crash"]).default("bump"),
  onCrash: z.enum(["reset_stage", "lose_life"]).default("reset_stage"),
  commands: z.array(z.enum(COMMANDS)).min(3).default([...COMMANDS]),
});

export type RobotRepairSettings = z.infer<typeof robotRepairSettingsSchema>;

export const robotRepairPayloadSchema = z.object({
  cols: z.number().int(),
  rows: z.number().int(),
  start: pointSchema,
  field: z.array(z.array(z.enum(["ok", "bad", "weak", "fixed"]))),
  stackCapacity: z.number().int(),
  stepMs: z.number().int(),
  edgeBehavior: z.enum(["bump", "crash"]),
  commands: z.array(z.enum(COMMANDS)),
});

export type RobotRepairPayload = z.infer<typeof robotRepairPayloadSchema>;

export const robotRepairSolutionSchema = z.object({
  program: z.array(z.enum(COMMANDS)).max(256),
});

export type RobotRepairSolution = z.infer<typeof robotRepairSolutionSchema>;

const FIELDS: SettingsField[] = [
  { path: "cols", label: "Столбцов", kind: "number", min: 3, max: 12, group: "Поле" },
  { path: "rows", label: "Строк", kind: "number", min: 3, max: 12, group: "Поле" },
  { path: "start", label: "Старт робота", kind: "point", group: "Поле" },
  { path: "badMin", label: "Разрушенных от", kind: "number", min: 0, group: "Поле" },
  { path: "badMax", label: "Разрушенных до", kind: "number", min: 0, group: "Поле" },
  { path: "weakMin", label: "Чинить от", kind: "number", min: 1, group: "Поле" },
  { path: "weakMax", label: "Чинить до", kind: "number", min: 1, group: "Поле" },
  { path: "solutionMin", label: "Длина решения от", kind: "number", min: 1, group: "Сложность" },
  { path: "solutionMax", label: "Длина решения до", kind: "number", min: 1, group: "Сложность" },
  { path: "stackCapacity", label: "Ячеек в стеке", kind: "number", min: 4, max: 64, group: "Пульт" },
  { path: "stepMs", label: "Шаг робота, мс", kind: "number", min: 80, max: 1200, step: 10, group: "Пульт" },
  {
    path: "edgeBehavior",
    label: "На краю диска",
    kind: "select",
    options: [
      { value: "bump", label: "Упереться и остаться на месте" },
      { value: "crash", label: "Разбиться" },
    ],
    group: "Правила",
  },
  {
    path: "onCrash",
    label: "При крахе",
    kind: "select",
    options: [
      { value: "reset_stage", label: "Сбросить этап" },
      { value: "lose_life", label: "Снять жизнь" },
    ],
    group: "Правила",
  },
  {
    path: "commands",
    label: "Доступные команды",
    kind: "multiselect",
    options: [
      { value: "up", label: "Вверх" },
      { value: "down", label: "Вниз" },
      { value: "left", label: "Влево" },
      { value: "right", label: "Вправо" },
      { value: "fix", label: "Ремонт" },
    ],
    group: "Пульт",
  },
];

type Point = { x: number; y: number };
type Field = SectorType[][];

const inside = (field: Field, x: number, y: number) =>
  y >= 0 && y < field.length && x >= 0 && x < field[0]!.length;

/** Расстояния и обратные ссылки от точки до всех секторов в обход разрушенных */
function bfs(field: Field, from: Point) {
  const rows = field.length;
  const cols = field[0]!.length;
  const dist: number[][] = Array.from({ length: rows }, () => Array(cols).fill(-1));
  const prev: (Command | null)[][] = Array.from({ length: rows }, () => Array(cols).fill(null));
  dist[from.y]![from.x] = 0;
  const queue: Point[] = [from];

  while (queue.length) {
    const { x, y } = queue.shift()!;
    for (const [cmd, [dx, dy]] of Object.entries(MOVES) as [Command, [number, number]][]) {
      const nx = x + dx;
      const ny = y + dy;
      if (!inside(field, nx, ny)) continue;
      if (field[ny]![nx] === "bad" || dist[ny]![nx] !== -1) continue;
      dist[ny]![nx] = dist[y]![x]! + 1;
      prev[ny]![nx] = cmd;
      queue.push({ x: nx, y: ny });
    }
  }
  return { dist, prev };
}

function permutations<T>(list: T[]): T[][] {
  if (list.length <= 1) return [list];
  return list.flatMap((item, i) =>
    permutations([...list.slice(0, i), ...list.slice(i + 1)]).map((rest) => [item, ...rest]),
  );
}

function pathCommands(prev: (Command | null)[][], from: Point, to: Point): Command[] {
  const out: Command[] = [];
  let { x, y } = to;
  while (!(x === from.x && y === from.y)) {
    const cmd = prev[y]![x];
    if (!cmd) return [];
    out.push(cmd);
    const [dx, dy] = MOVES[cmd as Exclude<Command, "fix">];
    x -= dx;
    y -= dy;
  }
  return out.reverse();
}

/** Кратчайшая программа: обойти все сбойные сектора и починить каждый. null — какой-то не достижим. */
export function shortestProgram(field: Field, start: Point, targets: Point[]): Command[] | null {
  const points = [start, ...targets];
  const maps = points.map((p) => bfs(field, p));
  const between = (a: number, b: number) => maps[a]!.dist[points[b]!.y]![points[b]!.x]!;
  if (targets.some((_, i) => between(0, i + 1) < 0)) return null;

  let best: Command[] | null = null;
  for (const order of permutations(targets.map((_, i) => i + 1))) {
    const program: Command[] = [];
    let at = 0;
    let broken = false;
    for (const next of order) {
      const leg = pathCommands(maps[at]!.prev, points[at]!, points[next]!);
      if (between(at, next) < 0) {
        broken = true;
        break;
      }
      program.push(...leg, "fix");
      at = next;
    }
    if (broken) continue;
    if (!best || program.length < best.length) best = program;
  }
  return best;
}

function generateField(settings: RobotRepairSettings, rng: Rng): { field: Field; program: Command[] } {
  const { cols, rows, start } = settings;

  for (let attempt = 0; attempt < 600; attempt++) {
    const field: Field = Array.from({ length: rows }, () => Array<SectorType>(cols).fill("ok"));
    const cells: Point[] = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        if (x !== start.x || y !== start.y) cells.push({ x, y });
      }
    }
    const order = rng.shuffle(cells);
    const badCount = rng.int(Math.min(settings.badMin, settings.badMax), Math.max(settings.badMin, settings.badMax));
    const weakCount = rng.int(
      Math.min(settings.weakMin, settings.weakMax),
      Math.max(settings.weakMin, settings.weakMax),
    );
    const bad = order.slice(0, badCount);
    const weak = order.slice(badCount, badCount + weakCount);
    if (weak.length < weakCount) continue;

    bad.forEach(({ x, y }) => {
      field[y]![x] = "bad";
    });
    weak.forEach(({ x, y }) => {
      field[y]![x] = "weak";
    });

    const program = shortestProgram(field, start, weak);
    if (!program) continue;
    if (program.length < settings.solutionMin || program.length > settings.solutionMax) continue;
    if (program.length > settings.stackCapacity) continue;
    return { field, program };
  }

  // запасной вариант: без разрушенных секторов поле заведомо проходимо
  const field: Field = Array.from({ length: rows }, () => Array<SectorType>(cols).fill("ok"));
  const spots: Point[] = [
    { x: Math.min(3, cols - 1), y: Math.min(2, rows - 1) },
    { x: Math.min(1, cols - 1), y: Math.min(4, rows - 1) },
    { x: Math.min(cols - 2, cols - 1), y: Math.min(rows - 2, rows - 1) },
  ].filter((p) => !(p.x === settings.start.x && p.y === settings.start.y));
  spots.forEach(({ x, y }) => {
    field[y]![x] = "weak";
  });
  return { field, program: shortestProgram(field, settings.start, spots) ?? [] };
}

function generate(
  settings: RobotRepairSettings,
  seed: number,
): GeneratedVariant<RobotRepairPayload, RobotRepairSolution> {
  const rng = new Rng(seed);
  const { field, program } = generateField(settings, rng);

  return {
    payload: {
      cols: settings.cols,
      rows: settings.rows,
      start: settings.start,
      field,
      stackCapacity: settings.stackCapacity,
      stepMs: settings.stepMs,
      edgeBehavior: settings.edgeBehavior,
      commands: settings.commands,
    },
    solution: { program },
    metrics: {
      solutionLength: program.length,
      bad: field.flat().filter((s) => s === "bad").length,
      weak: field.flat().filter((s) => s === "weak").length,
    },
  };
}

export interface RunResult {
  crashed: boolean;
  crashAt: Point | null;
  stoppedAt: number;
  robot: Point;
  field: Field;
  weakLeft: number;
  bumps: number;
  wastedFixes: number;
}

/** Тот же исполнитель, что крутит робота на экране: клиент показывает, сервер засчитывает. */
export function runProgram(payload: RobotRepairPayload, program: Command[]): RunResult {
  const field: Field = payload.field.map((row) => [...row]);
  const robot: Point = { ...payload.start };
  let bumps = 0;
  let wastedFixes = 0;

  for (let i = 0; i < program.length; i++) {
    const cmd = program[i]!;

    if (cmd === "fix") {
      if (field[robot.y]![robot.x] === "weak") field[robot.y]![robot.x] = "fixed";
      else wastedFixes++;
      continue;
    }

    const [dx, dy] = MOVES[cmd];
    const nx = robot.x + dx;
    const ny = robot.y + dy;

    if (!inside(field, nx, ny)) {
      if (payload.edgeBehavior === "crash") {
        return {
          crashed: true,
          crashAt: { x: nx, y: ny },
          stoppedAt: i,
          robot,
          field,
          weakLeft: field.flat().filter((s) => s === "weak").length,
          bumps,
          wastedFixes,
        };
      }
      bumps++;
      continue;
    }

    robot.x = nx;
    robot.y = ny;

    if (field[ny]![nx] === "bad") {
      return {
        crashed: true,
        crashAt: { x: nx, y: ny },
        stoppedAt: i,
        robot,
        field,
        weakLeft: field.flat().filter((s) => s === "weak").length,
        bumps,
        wastedFixes,
      };
    }
  }

  return {
    crashed: false,
    crashAt: null,
    stoppedAt: program.length,
    robot,
    field,
    weakLeft: field.flat().filter((s) => s === "weak").length,
    bumps,
    wastedFixes,
  };
}

function validate(
  payload: RobotRepairPayload,
  submitted: RobotRepairSolution,
  settings: RobotRepairSettings,
): ValidationResult {
  const program = submitted.program ?? [];
  if (!program.length) {
    return { solved: false, reason: "empty_program", mistakes: 0 };
  }
  if (program.length > Math.max(payload.stackCapacity, settings.stackCapacity)) {
    return { solved: false, reason: "stack_overflow", mistakes: 0 };
  }
  if (program.some((cmd) => !payload.commands.includes(cmd))) {
    return { solved: false, reason: "command_not_allowed", mistakes: 0 };
  }

  const run = runProgram(payload, program);
  const mistakes = run.bumps + run.wastedFixes + (run.crashed ? 1 : 0);

  if (run.crashed) {
    return {
      solved: false,
      reason: "crashed",
      mistakes,
      details: { crashAt: run.crashAt, stoppedAt: run.stoppedAt },
    };
  }
  if (run.weakLeft > 0) {
    return { solved: false, reason: "sectors_left", mistakes, details: { weakLeft: run.weakLeft } };
  }
  return { solved: true, mistakes, details: { steps: program.length } };
}

function minDurationMs(payload: RobotRepairPayload): number {
  // набрать программу и дождаться прогона: минимум — по шагу на клетку
  const weak = payload.field.flat().filter((s) => s === "weak").length;
  return Math.max(3000, weak * 2 * payload.stepMs + 2000);
}

function hint(payload: RobotRepairPayload, solution: RobotRepairSolution, used: number): Hint | null {
  const program = solution.program ?? [];
  if (!program.length) return null;
  // каждая подсказка открывает ещё три команды эталонной программы
  const show = Math.min(program.length, (used + 1) * 3);
  const labels: Record<Command, string> = {
    up: "вверх",
    down: "вниз",
    left: "влево",
    right: "вправо",
    fix: "ремонт",
  };
  const part = program.slice(0, show);
  return {
    kind: "program_prefix",
    data: { commands: part },
    text: `Начало программы: ${part.map((c) => labels[c]).join(" → ")}`,
  };
}

export const robotRepair: GameModule<RobotRepairSettings, RobotRepairPayload, RobotRepairSolution> = {
  code: "robot_repair",
  title: "Починка роботом",
  description: "Составить программу, которая обойдёт и починит сбойные сектора",
  settingsSchema: robotRepairSettingsSchema,
  payloadSchema: robotRepairPayloadSchema,
  solutionSchema: robotRepairSolutionSchema,
  defaultSettings: robotRepairSettingsSchema.parse({}),
  settingsFields: FIELDS,
  generate,
  validate,
  minDurationMs,
  hint,
};
